import { useState, useEffect, useRef } from 'react';
import { useDecks } from '../../hooks/useDecks';
import { useCreateGame, useJoinGame } from '../../hooks/useGame';
import { useWaitingGames } from '../../hooks/useGame';
import { supabase } from '../../lib/supabaseClient';
import { deleteGameSession } from '../../api/game';
import './GameMatchModal.css';

interface GameMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGameStart: (gameSessionId: string) => void;
}

type MatchStatus = 'selecting' | 'matching' | 'matched' | 'error';

export function GameMatchModal({ isOpen, onClose, onGameStart }: GameMatchModalProps) {
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [matchStatus, setMatchStatus] = useState<MatchStatus>('selecting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentGameSessionId, setCurrentGameSessionId] = useState<string | null>(null);
  const [realtimeChannel, setRealtimeChannel] = useState<any>(null);
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { data: decks, isLoading: decksLoading } = useDecks();
  const { data: waitingGames, refetch: refetchWaitingGames } = useWaitingGames(isOpen);
  const createGame = useCreateGame();
  const joinGame = useJoinGame();

  // 덱이 없으면 기본 덱 선택
  useEffect(() => {
    if (decks && decks.length > 0 && !selectedDeckId) {
      const activeDeck = decks.find((d) => d.is_active);
      if (activeDeck) {
        setSelectedDeckId(activeDeck.id);
      } else {
        setSelectedDeckId(decks[0].id);
      }
    }
  }, [decks, selectedDeckId]);

  // 매칭 시작
  const handleStartMatching = async () => {
    if (!selectedDeckId) {
      setErrorMessage('덱을 선택해주세요.');
      return;
    }

    setMatchStatus('matching');
    setErrorMessage(null);

    try {
      // 대기 중인 게임 조회
      const { data: games } = await refetchWaitingGames();
      const availableGames = games || [];

      if (availableGames.length > 0) {
        // 대기 중인 게임이 있으면 자동 참가
        const gameToJoin = availableGames[0];
        try {
          const joinedGame = await joinGame.mutateAsync({
            game_session_id: gameToJoin.id,
            deck_id: selectedDeckId,
          });
          setCurrentGameSessionId(joinedGame.id);
          setMatchStatus('matched');
          onGameStart(joinedGame.id);
        } catch (error) {
          // 참가 실패 시 새 게임 생성
          await createNewGame();
        }
      } else {
        // 대기 중인 게임이 없으면 새 게임 생성
        await createNewGame();
      }
    } catch (error) {
      setMatchStatus('error');
      setErrorMessage(error instanceof Error ? error.message : '매칭에 실패했습니다.');
    }
  };

  const createNewGame = async () => {
    const newGame = await createGame.mutateAsync({
      deck_id: selectedDeckId!,
    });
    setCurrentGameSessionId(newGame.id);

    // Realtime으로 상대방 참가 감지
    const channel = supabase
      .channel(`game-match:${newGame.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'game_sessions',
          filter: `id=eq.${newGame.id}`,
        },
        (payload) => {
          const updatedGame = payload.new as any;
          if (updatedGame.player2_id && updatedGame.status === 'playing') {
            setMatchStatus('matched');
            onGameStart(updatedGame.id);
            // 정리
            if (channel) {
              supabase.removeChannel(channel);
              setRealtimeChannel(null);
            }
            if (timeoutTimerRef.current) {
              clearTimeout(timeoutTimerRef.current);
              timeoutTimerRef.current = null;
            }
          }
        }
      )
      .subscribe();

    setRealtimeChannel(channel);

    // 30초 후 타임아웃
    timeoutTimerRef.current = setTimeout(() => {
      setMatchStatus((prevStatus) => {
        if (prevStatus === 'matching') {
          setErrorMessage('매칭 시간이 초과되었습니다. 다시 시도해주세요.');
          // 정리
          if (channel) {
            supabase.removeChannel(channel);
            setRealtimeChannel(null);
          }
          return 'error';
        }
        return prevStatus;
      });
    }, 30000);
  };

  // 매칭 중인 게임 세션 정리 함수
  const cleanupMatchingSession = async () => {
    // Realtime 채널 정리
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      setRealtimeChannel(null);
    }

    // 타임아웃 타이머 정리
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }

    // 대기 중인 게임 세션 삭제
    if (currentGameSessionId && matchStatus === 'matching') {
      try {
        await deleteGameSession(currentGameSessionId);
      } catch (error) {
        // 삭제 실패는 무시 (이미 다른 사용자가 참가했을 수 있음)
        console.error('Failed to delete game session:', error);
      }
    }
  };

  // 모달 닫기 시 상태 초기화 및 정리
  const handleClose = async () => {
    // 매칭 중인 경우 정리
    await cleanupMatchingSession();
    
    setMatchStatus('selecting');
    setSelectedDeckId(null);
    setErrorMessage(null);
    setCurrentGameSessionId(null);
    onClose();
  };

  // 모달이 열릴 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setMatchStatus('selecting');
      setErrorMessage(null);
      setCurrentGameSessionId(null);
    }
  }, [isOpen]);

  // 컴포넌트 언마운트 또는 모달 닫힐 때 정리
  useEffect(() => {
    return () => {
      // cleanup 함수에서 정리
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
      }
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }
      // 비동기 정리는 cleanup에서 처리하지 않음 (경고 방지)
    };
  }, [realtimeChannel]);

  if (!isOpen) return null;

  return (
    <div className="game-match-modal-overlay" onClick={handleClose}>
      <div className="game-match-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="game-match-modal__close"
          onClick={handleClose}
          aria-label="닫기"
          type="button"
        >
          ×
        </button>

        <h2 className="game-match-modal__title">⚔️ 대전 시작</h2>

        {matchStatus === 'selecting' && (
          <div className="game-match-modal__content">
            <div className="game-match-modal__section">
              <label className="game-match-modal__label">덱 선택</label>
              {decksLoading ? (
                <div className="game-match-modal__loading">덱 목록을 불러오는 중...</div>
              ) : !decks || decks.length === 0 ? (
                <div className="game-match-modal__error">
                  덱이 없습니다. 인벤토리에서 덱을 만들어주세요.
                </div>
              ) : (
                <div className="game-match-modal__deck-list">
                  {decks.map((deck) => (
                    <button
                      key={deck.id}
                      className={`game-match-modal__deck-item ${
                        selectedDeckId === deck.id ? 'active' : ''
                      }`}
                      onClick={() => setSelectedDeckId(deck.id)}
                      type="button"
                    >
                      <div className="game-match-modal__deck-name">{deck.name}</div>
                      <div className="game-match-modal__deck-count">
                        {deck.card_ids.length} / 30
                        {deck.is_active && (
                          <span className="game-match-modal__deck-badge">기본</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="game-match-modal__error-message">{errorMessage}</div>
            )}

            <button
              className="game-match-modal__start-btn"
              onClick={handleStartMatching}
              disabled={!selectedDeckId || decksLoading || createGame.isPending}
              type="button"
            >
              {createGame.isPending ? '게임 생성 중...' : '매칭 시작'}
            </button>
          </div>
        )}

        {matchStatus === 'matching' && (
          <div className="game-match-modal__matching">
            <div className="game-match-modal__matching-icon">🔍</div>
            <div className="game-match-modal__matching-text">상대를 찾는 중...</div>
            <div className="game-match-modal__matching-hint">
              잠시만 기다려주세요
            </div>
            <div className="game-match-modal__matching-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {matchStatus === 'error' && (
          <div className="game-match-modal__error-state">
            <div className="game-match-modal__error-icon">⚠️</div>
            <div className="game-match-modal__error-text">{errorMessage}</div>
            <button
              className="game-match-modal__retry-btn"
              onClick={() => setMatchStatus('selecting')}
              type="button"
            >
              다시 시도
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

